<template>
  <div
    v-show="loaded"
    id="dashboard-grid"
    class="grid-stack tailwind"
  >
    <widget-container
      v-for="(widget, index) in layout"
      :key="index"
      :editing="editing"
      :widget="widget"
      @edit-widget="widget => {editWidget(widget)}"
    />
  </div>

  <add-widget-modal
    v-if="addWidgetModal"
    @create="payload => {createCustomWidget()}"
    @selected="payload => {addDashWidget(payload)}"
  />

  <custom-widget-modal
    v-if="customWidgetModal"
  />

  <my-widgets-modal
    v-if="myWidgetsModal"
    @add-widget="payload => {addDashWidget(payload)}"
    @edit-widget="payload => {editCustomWidget(payload.widget)}"
    @create-widget="payload => {createCustomWidget()}"
  />

  <choose-competitors-modal
    v-if="chooseCompetitorsModal"
    @create-widget="payload => {addWidget(payload)}"
    @update-widget="payload => {updateWidget(payload)}"
  />

  <choose-location-modal
    v-if="chooseLocationModal"
    @create-widget="payload => {addWidget(payload)}"
    @update-widget="payload => {updateWidget(payload)}"
  />

  <choose-local-average-type-modal
    v-if="chooseLocalAverageTypeModal"
    @create-widget="payload => {addWidget(payload)}"
    @update-widget="payload => {updateWidget(payload)}"
  />
</template>

<script>
import {GridStack} from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.css';
import 'gridstack/dist/h5/gridstack-dd-native';

import WidgetContainer from './items/WidgetContainer.vue';
import AddWidgetModal from './modals/AddWidget.vue';
import CustomWidgetModal from './modals/CustomWidget.vue';
import MyWidgetsModal from './modals/MyWidgets.vue';
import ChooseCompetitorsModal from './modals/ChooseCompetitors.vue';
import ChooseLocationModal from './modals/ChooseLocation.vue';
import ChooseLocalAverageTypeModal from './modals/ChooseLocalAverageType.vue';

import {mapActions, mapMutations, mapState} from 'vuex';
import {nextTick} from 'vue';

export default {
  components: {
    WidgetContainer,
    AddWidgetModal,
    CustomWidgetModal,
    MyWidgetsModal,
    ChooseCompetitorsModal,
    ChooseLocationModal,
    ChooseLocalAverageTypeModal,
  },
  props: {
    editing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      grid: null,
      loaded: false,
      showModal: false,
    }
  },
  computed: {
    ...mapState('dashboard', ['layout', 'addWidgetModal', 'customWidgetModal', 'myWidgetsModal', 'chooseCompetitorsModal', 'chooseLocationModal', 'chooseLocalAverageTypeModal']),
  },
  async mounted() {
    this.init();
  },
  methods: {
    ...mapActions('dashboard', [
      'addWidget',
      'updateWidget',
      'closeAddWidgetModal',
      'openCustomWidgetModal',
      'openMyWidgetsModal',
      'editCustomWidget',
      'openChooseCompetitorsModal',
      'openChooseLocationModal',
      'openChooseLocalAverageTypeModal',
    ]),
    ...mapMutations('dashboard', ['setSelectedOption']),
    async init() {
      // Wait for layout to load
      if (this.layout.length > 0) {
        // Then wait for vue to render the widgets in the dom
        await nextTick();
        // Then build the gridstack grid
        this.buildGrid();
      } else {
        setTimeout(() => {
          this.init();
        }, 100);
      }
    },
    /**
     * Main function that builds the Gridstack grid, initialises all the widgets
     * and adds event listeners to each widget.
     *
     * @return  void
     */
    buildGrid() {
      let gridCellHeight = '450px';
      let column = 2;
      // Initialise Gridstack grid
      this.grid = GridStack.init({
        acceptWidgets: true,
        cellHeight: gridCellHeight,
        column: column,
        margin: '1rem',
        disableResize: true,
        staticGrid: !this.editing,
      }, 'dashboard-grid');

      this.grid.setStatic(!this.editing);

      // Loop through the widgets to update the elements with all of the widget data (attributes etc)
      this.layout.forEach(async widget => {
        var widgetEl = document.querySelector(`[gs-id="${widget.id}"`);
        this.grid.update(widgetEl, widget);
      });

      this.grid.compact();
      this.loaded = true;

      // Set Grid Events //
      this.grid.on('change', () => {
        this.grid.compact();
        let json = this.grid.save();
        json.forEach((widget) => {
          delete widget.content
        });
      });

      /**
       * Add border on the grid to highlight the boundaries of the grid
       * whenever a widget is dragged. Remove border when dragging is
       * stopped.
       */
      this.grid.on('dragstart', () => {
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = 'solid 1px #cc3131';
        });
      });
      this.grid.on('dragstop', () => {
        document.querySelectorAll('.grid-stack').forEach(element => {
          element.style.border = 'solid 1px transparent';
        });
      });

      // End Grid Events //
    },
    addDashWidget(option) {
      switch (option.next) {
        case 'chooseCompetitors':
          this.setSelectedOption(option);
          this.closeAddWidgetModal();
          this.openChooseCompetitorsModal();
          break;
        case 'chooseLocation':
          this.setSelectedOption(option);
          this.closeAddWidgetModal();
          this.openChooseLocationModal();
          break;
        case 'chooseLocalAverageType':
          this.setSelectedOption(option);
          this.closeAddWidgetModal();
          this.openChooseLocalAverageTypeModal();
          break;
        default:
          this.addWidget({widget: option.widget}).then(() => {
            this.SweetToast.fire({
              icon: 'success',
              title: 'Widget added successfully'
            });
          });
      }
    },
    createCustomWidget() {
      this.openCustomWidgetModal();
    },
    editWidget(widget) {
      switch (widget.editable) {
        case 'chooseCompetitors':
          this.setSelectedOption({widget: widget});
          this.openChooseCompetitorsModal();
          break;
        case 'chooseLocation':
          this.setSelectedOption({widget: widget});
          this.openChooseLocationModal();
          break;
        case 'chooseLocalAverageType':
          this.setSelectedOption({widget: widget});
          this.openChooseLocalAverageTypeModal();
          break;
      }
    },
  },
}
</script>

<style>
#dashboard-grid {
  margin: 0 -1rem;
  background: #f4f4f4;
}

#dashboard-grid .grid-stack-item .grid-stack-item-content {
  width: calc(100% - 2rem);
}
</style>
